import * as THREE from "three/webgpu";
import type { BattleTeam } from "./BattleInteractionRouter";

const MATRIX = new THREE.Matrix4();
const POSITION = new THREE.Vector3();
const QUATERNION = new THREE.Quaternion();
const SCALE = new THREE.Vector3();
const EULER = new THREE.Euler();
const COLOR = new THREE.Color();

export const FLOOD_DURATION = 3.6;
export const RIFT_DURATION = 4;

function hash(value: number): number {
  let result = value | 0;
  result = Math.imul(result ^ (result >>> 16), 0x21f0aaad);
  result = Math.imul(result ^ (result >>> 15), 0x735a2d97);
  return ((result ^ (result >>> 15)) >>> 0) / 0xffffffff;
}

function disposeMesh(mesh: THREE.Mesh | THREE.InstancedMesh): void {
  mesh.geometry.dispose();
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) material.dispose();
}

/** A travelling shallow-water bore with ballistic spray and an instanced crest. */
export class BattleFlood {
  readonly root = new THREE.Group();
  readonly #surface: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #body: THREE.InstancedMesh<THREE.BoxGeometry, THREE.MeshToonMaterial>;
  readonly #crest: THREE.InstancedMesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  readonly #foam: THREE.InstancedMesh<THREE.IcosahedronGeometry, THREE.MeshToonMaterial>;
  readonly #spray: THREE.InstancedMesh<THREE.IcosahedronGeometry, THREE.MeshBasicMaterial>;
  #target: BattleTeam = "aqua";
  #startedAt = -10;

  constructor(scene: THREE.Scene) {
    this.root.name = "battle-flood-physics";
    scene.add(this.root);

    this.#surface = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        color: 0x168fb6,
        transparent: true,
        opacity: 0.22,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    this.#surface.rotation.x = -Math.PI / 2;
    this.#surface.position.y = 0.075;
    this.#surface.renderOrder = 3;
    this.root.add(this.#surface);

    this.#body = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.56, 0.68, 0.52, 1, 2, 1),
      new THREE.MeshToonMaterial({ color: 0xffffff, transparent: true, opacity: 0.72, vertexColors: true }),
      24,
    );
    this.#body.name = "flood-instanced-water-body";
    this.#body.frustumCulled = false;
    this.root.add(this.#body);

    this.#crest = new THREE.InstancedMesh(
      new THREE.TorusGeometry(0.42, 0.09, 5, 12, Math.PI * 1.2),
      new THREE.MeshBasicMaterial({ color: 0xc9ffff, transparent: true, opacity: 0.82, depthWrite: false, blending: THREE.AdditiveBlending }),
      24,
    );
    this.#crest.name = "flood-instanced-crest";
    this.#crest.frustumCulled = false;
    this.#crest.renderOrder = 7;
    this.root.add(this.#crest);

    this.#foam = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(0.09, 1),
      new THREE.MeshToonMaterial({ color: 0xeaffff }),
      88,
    );
    this.#foam.name = "flood-instanced-foam";
    this.#foam.frustumCulled = false;
    this.root.add(this.#foam);

    this.#spray = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(0.045, 1),
      new THREE.MeshBasicMaterial({ color: 0x9cf5ff, transparent: true, opacity: 0.86, depthWrite: false, blending: THREE.AdditiveBlending }),
      112,
    );
    this.#spray.name = "flood-ballistic-spray";
    this.#spray.frustumCulled = false;
    this.#spray.renderOrder = 8;
    this.root.add(this.#spray);
    this.root.visible = false;
  }

  trigger(target: BattleTeam, time: number): void {
    this.#target = target;
    this.#startedAt = time;
  }

  update(time: number, frontline: number, reducedMotion: boolean): void {
    const age = time - this.#startedAt;
    const active = age >= 0 && age < FLOOD_DURATION;
    this.root.visible = active;
    if (!active) return;

    const sign = this.#target === "ember" ? -1 : 1;
    const progress = THREE.MathUtils.clamp(age / FLOOD_DURATION, 0, 1);
    const travel = THREE.MathUtils.smoothstep(progress, 0.02, 0.92);
    const waveX = frontline + sign * (0.25 + travel * 4.25);
    const fade = 1 - THREE.MathUtils.smoothstep(progress, 0.78, 1);
    const surge = Math.sin(Math.min(1, progress * 2.4) * Math.PI * 0.5);

    const floodedWidth = Math.max(0.12, Math.abs(waveX - frontline));
    this.#surface.position.x = (waveX + frontline) * 0.5;
    this.#surface.scale.set(floodedWidth, 7.55, 1);
    this.#surface.material.opacity = 0.18 * fade;

    const waterCount = reducedMotion ? 12 : 24;
    this.#body.count = waterCount;
    this.#crest.count = waterCount;
    for (let index = 0; index < waterCount; index += 1) {
      const lane = index / (waterCount - 1);
      const z = THREE.MathUtils.lerp(-3.55, 3.55, lane);
      const chop = Math.sin(time * 9.5 + index * 1.73) * 0.13;
      POSITION.set(waveX + sign * chop * 0.25, 0.36 + surge * 0.3 + chop, z);
      EULER.set(0, 0, sign * (-0.18 - surge * 0.2));
      QUATERNION.setFromEuler(EULER);
      SCALE.set(1 + surge * 0.35, 0.72 + surge * 0.72, 0.9);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#body.setMatrixAt(index, MATRIX);
      this.#body.setColorAt(index, COLOR.set(index % 3 === 0 ? 0x126e9d : index % 2 ? 0x22b6cb : 0x36d7d5));

      POSITION.set(waveX - sign * 0.16, 0.72 + surge * 0.5 + chop, z);
      EULER.set(0, sign > 0 ? Math.PI / 2 : -Math.PI / 2, sign * 0.18);
      QUATERNION.setFromEuler(EULER);
      SCALE.set(0.9 + surge * 0.55, 1, 1.05);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#crest.setMatrixAt(index, MATRIX);
    }
    this.#body.instanceMatrix.needsUpdate = true;
    if (this.#body.instanceColor) this.#body.instanceColor.needsUpdate = true;
    this.#crest.instanceMatrix.needsUpdate = true;
    this.#body.material.opacity = 0.72 * fade;
    this.#crest.material.opacity = 0.82 * fade;

    const foamCount = reducedMotion ? 32 : 88;
    this.#foam.count = foamCount;
    for (let index = 0; index < foamCount; index += 1) {
      const seed = hash(index * 101 + 17);
      const z = -3.65 + hash(index * 53 + 7) * 7.3;
      const localAge = (time * (0.9 + seed * 0.4) + seed * 3.7) % 1;
      const x = waveX - sign * (0.08 + localAge * (0.45 + seed * 0.7));
      const y = 0.26 + Math.sin(localAge * Math.PI) * (0.42 + seed * 0.46);
      POSITION.set(x, y, z);
      QUATERNION.identity();
      const size = (0.5 + seed * 1.15) * fade;
      SCALE.set(size * 1.4, size * 0.55, size);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#foam.setMatrixAt(index, MATRIX);
    }
    this.#foam.instanceMatrix.needsUpdate = true;

    const sprayCount = reducedMotion ? 38 : 112;
    this.#spray.count = sprayCount;
    for (let index = 0; index < sprayCount; index += 1) {
      const seed = hash(index * 83 + 29);
      const lifetime = 0.72 + seed * 0.58;
      const particleAge = (age + seed * lifetime * 1.8) % lifetime;
      const vx = 0.5 + seed * 1.5;
      const vy = 2.8 + hash(index * 41 + 5) * 2.9;
      const y = 0.48 + vy * particleAge - 4.9 * particleAge * particleAge;
      POSITION.set(
        waveX - sign * vx * particleAge,
        Math.max(0.08, y),
        -3.5 + hash(index * 67 + 11) * 7 + Math.sin(time * 4 + seed * 18) * 0.12,
      );
      EULER.set(time * 2, seed * 8, time * 1.3);
      QUATERNION.setFromEuler(EULER);
      const size = (0.62 + seed * 1.65) * fade;
      SCALE.set(size * 0.55, size * 1.8, size * 0.55);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#spray.setMatrixAt(index, MATRIX);
    }
    this.#spray.instanceMatrix.needsUpdate = true;
    this.#spray.material.opacity = 0.86 * fade;
  }

  dispose(): void {
    for (const mesh of [this.#surface, this.#body, this.#crest, this.#foam, this.#spray]) disposeMesh(mesh);
    this.root.removeFromParent();
  }
}

/** Instanced rug/terrain plates driven by a propagating fracture wave. */
export class BattleRift {
  readonly root = new THREE.Group();
  readonly #plates: THREE.InstancedMesh<THREE.BoxGeometry, THREE.MeshToonMaterial>;
  readonly #glow: THREE.InstancedMesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>;
  readonly #rocks: THREE.InstancedMesh<THREE.TetrahedronGeometry, THREE.MeshToonMaterial>;
  readonly #rings: THREE.InstancedMesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  #target: BattleTeam = "aqua";
  #startedAt = -10;

  constructor(scene: THREE.Scene) {
    this.root.name = "battle-rift-physics";
    scene.add(this.root);

    this.#plates = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.58, 0.16, 0.58),
      new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }),
      72,
    );
    this.#plates.name = "rift-instanced-terrain-plates";
    this.#plates.frustumCulled = false;
    this.#plates.castShadow = true;
    this.root.add(this.#plates);

    this.#glow = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.7, 0.025, 0.07),
      new THREE.MeshBasicMaterial({ color: 0xffd36a, transparent: true, opacity: 0.82, depthWrite: false, blending: THREE.AdditiveBlending }),
      48,
    );
    this.#glow.name = "rift-instanced-crack-glow";
    this.#glow.frustumCulled = false;
    this.#glow.renderOrder = 8;
    this.root.add(this.#glow);

    this.#rocks = new THREE.InstancedMesh(
      new THREE.TetrahedronGeometry(0.14, 0),
      new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }),
      80,
    );
    this.#rocks.name = "rift-ballistic-rocks";
    this.#rocks.frustumCulled = false;
    this.#rocks.castShadow = true;
    this.root.add(this.#rocks);

    this.#rings = new THREE.InstancedMesh(
      new THREE.TorusGeometry(1, 0.035, 5, 48),
      new THREE.MeshBasicMaterial({ color: 0xffe6a0, transparent: true, opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending }),
      4,
    );
    this.#rings.name = "rift-shockwave-rings";
    this.#rings.frustumCulled = false;
    this.#rings.renderOrder = 9;
    this.root.add(this.#rings);
    this.root.visible = false;
  }

  trigger(target: BattleTeam, time: number): void {
    this.#target = target;
    this.#startedAt = time;
  }

  update(time: number, frontline: number, reducedMotion: boolean): void {
    const age = time - this.#startedAt;
    const active = age >= 0 && age < RIFT_DURATION;
    this.root.visible = active;
    if (!active) return;

    const sign = this.#target === "ember" ? -1 : 1;
    const centerX = frontline + sign * 2.05;
    const progress = THREE.MathUtils.clamp(age / RIFT_DURATION, 0, 1);
    const grow = THREE.MathUtils.smoothstep(progress, 0, 0.2);
    const settle = 1 - THREE.MathUtils.smoothstep(progress, 0.68, 1);
    const strength = grow * settle;

    const plateCount = reducedMotion ? 32 : 72;
    this.#plates.count = plateCount;
    for (let index = 0; index < plateCount; index += 1) {
      const column = index % 9;
      const row = Math.floor(index / 9);
      const xSeed = hash(index * 59 + 3);
      const zSeed = hash(index * 73 + 19);
      const x = frontline + sign * (0.35 + column * 0.48 + xSeed * 0.18);
      const z = -3.35 + row * 0.93 + zSeed * 0.22;
      const ridge = Math.max(0, 1 - Math.abs(x - centerX) / 2.35);
      const activation = THREE.MathUtils.smoothstep(progress * 5.2 - Math.abs(x - centerX), -0.35, 0.35);
      const lift = ridge * activation * strength * (0.32 + xSeed * 0.72);
      POSITION.set(x, 0.04 + lift * 0.52, z);
      EULER.set(zSeed * lift * 0.38, xSeed * 0.3, sign * (xSeed - 0.5) * lift * 0.72);
      QUATERNION.setFromEuler(EULER);
      SCALE.set(1, 1 + lift * 2.2, 1);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#plates.setMatrixAt(index, MATRIX);
      this.#plates.setColorAt(index, COLOR.set(index % 3 === 0 ? 0x574044 : index % 2 ? 0x80634c : 0x4b5960));
    }
    this.#plates.instanceMatrix.needsUpdate = true;
    if (this.#plates.instanceColor) this.#plates.instanceColor.needsUpdate = true;

    const crackCount = reducedMotion ? 22 : 48;
    this.#glow.count = crackCount;
    for (let index = 0; index < crackCount; index += 1) {
      const branch = index % 3;
      const along = index / crackCount;
      const x = frontline + sign * (0.2 + along * 4.5);
      const z = (branch - 1) * 1.34 + Math.sin(index * 2.31) * (0.18 + along * 0.52);
      const width = (0.25 + hash(index * 31 + 2) * 0.58) * grow;
      POSITION.set(x, 0.105, z);
      EULER.set(0, sign * (Math.sin(index * 1.7) * 0.45), 0);
      QUATERNION.setFromEuler(EULER);
      SCALE.set(width, 1, 1);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#glow.setMatrixAt(index, MATRIX);
    }
    this.#glow.instanceMatrix.needsUpdate = true;
    this.#glow.material.opacity = 0.82 * settle;

    const rockCount = reducedMotion ? 28 : 80;
    this.#rocks.count = rockCount;
    for (let index = 0; index < rockCount; index += 1) {
      const seed = hash(index * 97 + 13);
      const lifetime = 0.82 + seed * 0.72;
      const localAge = Math.min(lifetime, Math.max(0, age - hash(index * 43 + 7) * 0.65));
      const launchX = centerX + sign * (seed - 0.5) * 3.4;
      const launchZ = -3.25 + hash(index * 61 + 23) * 6.5;
      const vx = sign * (seed - 0.38) * 1.3;
      const vz = (hash(index * 79 + 29) - 0.5) * 2.2;
      const vy = 3.1 + hash(index * 37 + 31) * 3.8;
      POSITION.set(
        launchX + vx * localAge,
        0.12 + Math.max(0, vy * localAge - 5.6 * localAge * localAge),
        launchZ + vz * localAge,
      );
      EULER.set(time * (1.4 + seed * 3), seed * 9 + time * 2, time * (0.8 + seed * 2.2));
      QUATERNION.setFromEuler(EULER);
      const size = (0.62 + seed * 1.8) * settle;
      SCALE.set(size, size * 1.4, size);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#rocks.setMatrixAt(index, MATRIX);
      this.#rocks.setColorAt(index, COLOR.set(index % 3 === 0 ? 0xd7a861 : index % 2 ? 0x6b4d48 : 0x384650));
    }
    this.#rocks.instanceMatrix.needsUpdate = true;
    if (this.#rocks.instanceColor) this.#rocks.instanceColor.needsUpdate = true;

    for (let index = 0; index < this.#rings.count; index += 1) {
      const ringAge = Math.max(0, age - index * 0.12);
      const ringProgress = THREE.MathUtils.clamp(ringAge / 1.05, 0, 1);
      POSITION.set(centerX, 0.12 + index * 0.02, 0);
      EULER.set(Math.PI / 2, 0, 0);
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar((0.25 + ringProgress * 3.8) * strength);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#rings.setMatrixAt(index, MATRIX);
    }
    this.#rings.instanceMatrix.needsUpdate = true;
    this.#rings.material.opacity = 0.62 * settle;
  }

  dispose(): void {
    for (const mesh of [this.#plates, this.#glow, this.#rocks, this.#rings]) disposeMesh(mesh);
    this.root.removeFromParent();
  }
}
